import React from 'react';

import { NotSet } from '../HomeSection';

import { indicator } from 'Helpers/currencyFormat';
import { formatFigure, formatToFixedMoneyWithLocale, ZERO_DASH } from 'Helpers/formatService';

import Badge from 'CommonComponents/Badge';

/**
 * The pills a Home list row shows in its status, priority and RAG columns.
 *
 * ALL FIVE VARIANTS COME FROM CommonComponents/Badge, which is already built on
 * the project's own tokens. Nothing here introduces a colour.
 *
 * A VARIANT IS NEVER CHOSEN BY MATCHING TRANSLATED TEXT. `status` and
 * `priority` arrive translated, so comparing them against "Unsigned" or "High"
 * would quietly stop colouring anything the moment the page is served in
 * Arabic. Each helper below reads a structural field — a boolean flag, or the
 * untranslated 'R'/'A'/'G' and 'low'/'medium'/'high' keys — and renders the
 * translated text inside the pill it chose.
 */

/**
 * A R.A.D.I.O. or procurement status: event-configurable free text with no
 * open/closed flag behind it, so there is no state for a colour to signify and
 * every one of them is neutral.
 */
export function StatusBadge({ status }) {
    if (!status) {
        return <NotSet />;
    }

    return <Badge variant="neutral">{status}</Badge>;
}

const PRIORITY_VARIANTS = {
    high: 'danger',
    medium: 'warning',
    low: 'neutral',
};

const PRIORITY_LABELS = {
    high: () => $t('High'),
    medium: () => $t('Medium'),
    low: () => $t('Low'),
};

/** An issue's or decision's priority — one of three known keys, or free text. */
export function PriorityBadge({ priority }) {
    if (!priority) {
        return <NotSet />;
    }

    const key = String(priority).toLowerCase();

    return <Badge variant={PRIORITY_VARIANTS[key] ?? 'neutral'}>{PRIORITY_LABELS[key]?.() ?? priority}</Badge>;
}

const RAG_VARIANTS = {
    R: 'danger',
    A: 'warning',
    G: 'success',
};

/**
 * Whether a risk's classification is red — the exception the risks list marks.
 *
 * Asked of the RAW classification only. Raw is what the register leads with,
 * what HomeDeltasResolver::risksRed() counts, and therefore what the "N rated
 * red" attention card is about; marking the controlled reading too would put a
 * rule beside rows that figure does not count.
 *
 * Compared against the untranslated 'R' the server sends, for the reason at the
 * top of this file.
 */
export const isRedRag = (rag) => String(rag ?? '').toUpperCase() === 'R';

const RAG_LABELS = {
    R: () => $t('Red'),
    A: () => $t('Amber'),
    G: () => $t('Green'),
};

/**
 * A risk's classification, as a LABELLED pill rather than a bare block of
 * colour.
 *
 * The register renders this cell as nothing but a background colour, which
 * announces nothing at all to a screen reader and means nothing to a
 * colour-blind reader — the old table worked around it with a visually hidden
 * letter. The pill carries the word, so there is nothing left to hide.
 */
export function RagBadge({ rag }) {
    if (!rag) {
        return <NotSet />;
    }

    const key = String(rag).toUpperCase();

    return <Badge variant={RAG_VARIANTS[key] ?? 'neutral'}>{RAG_LABELS[key]?.() ?? rag}</Badge>;
}

/**
 * A contract's state.
 *
 * Keyed off `unsigned`, the row's own boolean, for the reason at the top of this
 * file — the `status` string beside it is translated. A signed or closed
 * contract is nothing going wrong, so it reads neutral; only the missing
 * signature is an exception, and it is the figure the contracts tile leads with.
 */
export function ContractStatusBadge({ status, unsigned }) {
    if (!status) {
        return <NotSet />;
    }

    return <Badge variant={unsigned ? 'danger' : 'neutral'}>{status}</Badge>;
}

/**
 * A "total (outstanding)" action figure: the dash when the record has no actions,
 * never "0 (0)", and blank when no count was sent. Outstanding is dropped on its
 * own when it is zero: the actions exist and are all done, which "4" says without
 * the bracket implying otherwise.
 */
export function ActionCounts({ total, outstanding }) {
    if (!total) {
        return formatFigure(total, String);
    }

    return (
        <>
            {total}
            {outstanding > 0 && <span className="tw-text-charcoal-60"> ({outstanding})</span>}
        </>
    );
}

/** A value cell: blank when none was sent, the dash for a zero, otherwise the money with its currency indicator. */
export function moneyFigure(value) {
    const text = formatFigure(value, formatToFixedMoneyWithLocale);

    return text === '' || text === ZERO_DASH ? text : indicator(text);
}
